import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
// Optimized imports to prevent build crashes
import { FiPhone, FiMessageCircle, FiCheck, FiClock, FiShield, FiUsers, FiMapPin, FiStar, FiChevronDown, FiMenu, FiX, FiEdit } from 'react-icons/fi';
import { FaHeart, FaBaby, FaUserMd, FaHome, FaCheckCircle, FaPhone } from 'react-icons/fa';
import SafeIcon from './common/SafeIcon';
import SocialProofPopup from './components/SocialProofPopup';
import KeywordMarquee from './components/KeywordMarquee';
import './App.css';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', mobile: '', area: '', service: '', timing: '', requirement: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // Format mobile number to only allow digits
    if (name === 'mobile') {
      // Remove non-numeric characters
      const numericValue = value.replace(/[^\d]/g, '');
      setFormData({ ...formData, [name]: numericValue });
      // Clear mobile error when user starts typing
      if (formErrors.mobile && numericValue.length > 0) {
        setFormErrors({ ...formErrors, mobile: '' });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    const errors = {};
    // Mobile number validation (COMPULSORY)
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (formData.mobile.length !== 10) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Please enter your full name';
    }
    // Area validation
    if (!formData.area.trim()) {
      errors.area = 'Please enter your area in Mumbai';
    }
    // Service validation
    if (!formData.service) {
      errors.service = 'Please select a service';
    }
    // Timing validation
    if (!formData.timing) {
      errors.timing = 'Please select timing preference';
    }
    return errors;
  };

const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  const errors = validateForm();

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    setIsSubmitting(false);
    return;
  }

  setFormErrors({});

  try {

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("mobile", formData.mobile);
    formDataToSend.append("area", formData.area);
    formDataToSend.append("service", formData.service);
    formDataToSend.append("timing", formData.timing);
    formDataToSend.append("requirement", formData.requirement || "");
    formDataToSend.append("_subject", `New Caregiver Request - ${formData.service}`);
    formDataToSend.append("_captcha", "false");
    formDataToSend.append("_template", "table");

    const response = await fetch("https://formsubmit.co/ajax/shauryaagency2018@gmail.com", {
      method: "POST",
      body: formDataToSend
    });

    const result = await response.json();

    if (result.success) {

      setSubmissionSuccess(true);

      setFormData({
        name: "",
        mobile: "",
        area: "",
        service: "",
        timing: "",
        requirement: ""
      });

      if (showFormModal) {
        setTimeout(() => {
          closeFormModal();
        }, 2000);
      }

      setTimeout(() => {
        setSubmissionSuccess(false);
      }, 3000);

    } else {
      throw new Error("Form submission failed");
    }

  } catch (error) {

    console.error("Form submit error:", error);

    alert(
      "There was an error submitting your request. Please try again or call us directly at +91 98923 30344"
    );

  } finally {
    setIsSubmitting(false);
  }
};
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleBookNow = (serviceType) => {
    setFormData({ ...formData, service: serviceType });
    // On mobile/tablet, open modal instead of scrolling to hero
    if (isMobile) {
      openFormModal();
    } else {
      scrollToSection('home');
    }
  };

  const openFormModal = () => {
    setShowFormModal(true);
    setIsClosing(false);
    document.body.style.overflow = 'hidden';
  };

  const closeFormModal = () => {
    setIsClosing(true);
    document.body.style.overflow = '';
    setTimeout(() => {
      setShowFormModal(false);
      setIsClosing(false);
    }, 300);
  };

  const renderForm = () => (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      {/* Success Message */}
      {submissionSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800 font-medium">
              Thank you! Your request has been sent successfully. We'll contact you within 30 minutes.
            </p>
          </div>
        </motion.div>
      )}

      {/* Name Field */}
      <div>
        <input
          type="text"
          name="name"
          placeholder="Full Name"
          value={formData.name}
          onChange={handleFormChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
          required
        />
        {formErrors.name && (
          <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
        )}
      </div>

      {/* Mobile Number Field */}
      <div>
        <input
          type="tel"
          name="mobile"
          placeholder="Mobile Number (10 digits)"
          value={formData.mobile}
          onChange={handleFormChange}
          maxLength="10"
          pattern="[0-9]{10}"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formErrors.mobile ? 'border-red-500' : 'border-gray-300'}`}
          required
        />
        {formErrors.mobile && (
          <p className="text-red-500 text-sm mt-1">{formErrors.mobile}</p>
        )}
      </div>

      {/* Area Field */}
      <div>
        <input
          type="text"
          name="area"
          placeholder="Area in Mumbai (e.g., Andheri, Bandra)"
          value={formData.area}
          onChange={handleFormChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formErrors.area ? 'border-red-500' : 'border-gray-300'}`}
          required
        />
        {formErrors.area && (
          <p className="text-red-500 text-sm mt-1">{formErrors.area}</p>
        )}
      </div>

      {/* Service Dropdown */}
      <div>
        <select
          name="service"
          value={formData.service}
          onChange={handleFormChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formErrors.service ? 'border-red-500' : 'border-gray-300'}`}
          required
        >
          <option value="">Select Service Type</option>
          <option value="elderly">Elderly Care</option>
          <option value="patient">Patient Care</option>
          <option value="baby">Baby Care</option>
          <option value="japa">Japa Maid</option>
          <option value="nurse">Nursing Services</option>
        </select>
        {formErrors.service && (
          <p className="text-red-500 text-sm mt-1">{formErrors.service}</p>
        )}
      </div>

      {/* Timing Dropdown */}
      <div>
        <select
          name="timing"
          value={formData.timing}
          onChange={handleFormChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formErrors.timing ? 'border-red-500' : 'border-gray-300'}`}
          required
        >
          <option value="">Timing Preference</option>
          <option value="12-hours">12 Hours</option>
          <option value="24-hours">24 Hours</option>
          <option value="hourly">Hourly Visits</option>
          <option value="live-in">Live-in Care</option>
        </select>
        {formErrors.timing && (
          <p className="text-red-500 text-sm mt-1">{formErrors.timing}</p>
        )}
      </div>

      {/* Additional Requirements (not required) */}
      <div>
        <textarea
          name="requirement"
          placeholder="Additional requirements or special needs (optional)"
          value={formData.requirement}
          onChange={handleFormChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          rows={3}
        />
        <p className="text-xs text-gray-500 mt-1">Please mention any specific requirements, medications, or special care needs</p>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full bg-blue-600 text-white py-4 rounded-lg font-semibold transition-colors shadow-lg flex items-center justify-center ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
      >
        {isSubmitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Sending Request...
          </>
        ) : (
          'Get Caregiver Details – FREE Consultation'
        )}
      </button>

      {/* Form submission error */}
      {formErrors.submit && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiX} className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 font-medium">
              {formErrors.submit}
            </p>
          </div>
        </motion.div>
      )}
    </form>
  );

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Social Proof Popup */}
      <SocialProofPopup />

      {/* Keyword Marquee */}
      <KeywordMarquee />

      {/* Modal Overlay */}
      {showFormModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: isClosing ? 0.8 : 1, opacity: isClosing ? 0 : 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md mx-auto relative"
          >
            <button
              onClick={closeFormModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors z-10"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>

            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Book Your Caregiver Today – FREE Consultation
              </h3>
              {renderForm()}
              <p className="text-sm text-gray-600 text-center mt-4">
                <SafeIcon icon={FiShield} className="w-4 h-4 inline mr-1 text-green-500" />
                100% verified caregivers • Response within 30 minutes • Free replacement guarantee
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-white shadow-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0 flex items-center">
              <img src="https://i.ibb.co/mr83wr4d/image2.png" alt="Shaurya Caretaker" className="h-14 w-auto md:h-16" />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {['Home', 'Services', 'Reviews'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  {item}
                </button>
              ))}
            </nav>

            <div className="flex items-center space-x-4">
              <a
                href="tel:+919892330344"
                className="hidden sm:flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <span>+91 98923 30344</span>
              </a>

              {/* Get Free Consultation Button - Desktop */}
              <button
                onClick={openFormModal}
                className="hidden md:flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                <span>Get Free Consultation</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6 text-blue-600" />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden bg-white border-t border-gray-100 py-4"
            >
              <nav className="flex flex-col space-y-3">
                {['Home', 'Services', 'Reviews'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="text-left text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    {item}
                  </button>
                ))}
                <a
                  href="tel:+919892330344"
                  className="flex items-center space-x-2 text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <SafeIcon icon={FiPhone} className="w-4 h-4" />
                  <span>+91 98923 30344</span>
                </a>
                <button
                  onClick={openFormModal}
                  className="flex items-center space-x-2 text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <SafeIcon icon={FiEdit} className="w-4 h-4" />
                  <span>Get Free Consultation</span>
                </button>
              </nav>
            </motion.div>
          )}
        </div>
      </header>

      {/* Hero Section with Updated Background Image */}
      <section id="home" className="relative py-16 lg:py-24">
        {/* Updated Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: 'url("https://i.ibb.co/1GCdtZ9b/Gemini-Generated-Image-jceieljceieljce.jpg")' }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8 text-white"
            >
              <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
                Mumbai's Trusted Nursing Bureau in Mumbai
              </div>

              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                Professional <span className="text-blue-400">Home Care</span> Services in Mumbai
              </h1>

              <p className="text-xl text-gray-200 leading-relaxed">
                Expert care givers at home for caretaker elderly/patient and babies. Trusted by 1,000+ Mumbai families with 24/7 availability and quick placement.
              </p>

              <ul className="space-y-4">
                {[
                  { text: 'Verified & trained caretakers for elderly in Mumbai', icon: FiShield },
                  { text: 'Elderly, patient & baby care', icon: FaHeart },
                  { text: '24/7 availability across Mumbai', icon: FiClock },
                  { text: 'Quick placement within 24 hours', icon: FaCheckCircle }
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={item.icon} className="w-5 h-5 text-green-400" />
                    <span className="text-white font-medium">{item.text}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+919892330344"
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg"
                >
                  <SafeIcon icon={FaPhone} className="w-5 h-5" />
                  <span>Call for Consultation</span>
                </a>

                {/* Mobile/Tablet: Show modal button, Desktop: Show form button */}
                {isMobile ? (
                  <button
                    onClick={openFormModal}
                    className="flex items-center justify-center space-x-2 bg-green-500 text-white px-8 py-4 rounded-lg hover:bg-green-600 transition-colors font-semibold shadow-lg w-full"
                  >
                    <SafeIcon icon={FiEdit} className="w-5 h-5" />
                    <span>Get Free Consultation</span>
                  </button>
                ) : (
                  <button
                    onClick={openFormModal}
                    className="flex items-center justify-center space-x-2 bg-green-500 text-white px-8 py-4 rounded-lg hover:bg-green-600 transition-colors font-semibold shadow-lg"
                  >
                    <SafeIcon icon={FiEdit} className="w-5 h-5" />
                    <span>Get Free Consultation</span>
                  </button>
                )}
              </div>
            </motion.div>

            {/* Right Side - Lead Form (Desktop only) */}
            {!isMobile && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Book Your Patient Care Taker Services in Mumbai Today – FREE Consultation
                </h3>
                {renderForm()}
                <p className="text-sm text-gray-600 text-center mt-4">
                  <SafeIcon icon={FiShield} className="w-4 h-4 inline mr-1 text-green-500" />
                  100% verified caregivers • Quick response guaranteed • Free replacement
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Trust Stats Strip */}
      <section className="bg-blue-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
            {[
              { icon: FiClock, text: '10+ Years Experience', subtext: 'Est. 2014' },
              { icon: FaHeart, text: '1,000+ Happy Families', subtext: 'Trusted Choice' },
              { icon: FiShield, text: 'Verified Staff', subtext: 'Background Checked' },
              { icon: FiPhone, text: '24/7 Support', subtext: 'Always Available' },
              { icon: FiMapPin, text: 'All Mumbai Coverage', subtext: 'Every Suburb' }
            ].map((stat, index) => (
              <div key={index} className="space-y-2">
                <SafeIcon icon={stat.icon} className="w-8 h-8 mx-auto text-blue-200" />
                <p className="text-lg font-semibold">{stat.text}</p>
                <p className="text-blue-200 text-sm">{stat.subtext}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Care Do You Need Today */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12">
            What Care Do You Need Today?
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Elderly & Patient Care Card */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FaHeart} className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Elderly & Patient Care</h3>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'Experienced caretaker elderly/patient',
                  'Post-surgery patient caretaker in Mumbai',
                  'Chronic illness support',
                  'Medication management',
                  'Companionship services',
                  'Mobility assistance'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleBookNow('elderly')}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
              >
                Get Caretaker Near Me
              </button>
            </motion.div>

            {/* Baby & Mother Care Card */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-pink-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FaBaby} className="w-7 h-7 text-pink-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Baby & Mother Care</h3>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'Experienced Japa maids',
                  'Newborn baby care in Mumbai',
                  'New mother support',
                  'Feeding & sleep training',
                  'Household assistance',
                  'Nutrition guidance'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleBookNow('japa')}
                className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors shadow-md"
              >
                Get Japa Maid Near Me
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why 1,000+ Mumbai Families Trust Us */}
      <section id="services" className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12">
            Why 1,000+ Mumbai Families Trust Us
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: FiShield, title: '100% Verified Staff', description: 'Every caregiver undergoes thorough background verification, police verification, and reference checks' },
              { icon: FaUserMd, title: 'Trained & Experienced', description: 'Professionally trained caregivers with years of experience in healthcare and patient care' },
              { icon: FiClock, title: 'Quick Placement', description: 'Get your perfect caregiver match within 24 hours of your requirement' },
              { icon: FiUsers, title: 'Free Replacement', description: 'Not satisfied? We provide immediate free replacement with no questions asked' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                className="bg-white p-6 rounded-xl shadow-md border border-gray-100 text-center hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Complete Home Care Solutions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12">
            Complete Home Care Solutions
          </h2>

          {/* Elderly & Patient Care Section */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-gray-900 mb-8 text-center">Elderly & Patient Care</h3>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Old Age Care Taker at Home</h4>
                <ul className="space-y-2 mb-6">
                  {[
                    'Daily living assistance',
                    'Medication reminders',
                    'Mobility support',
                    'Companionship',
                    'Meal preparation',
                    'Personal care assistance'
                  ].map((service, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleBookNow('elderly')}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Book In Home Elderly Care in Mumbai
                </button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Home Nursing Services (GNM/ANM)</h4>
                <ul className="space-y-2 mb-6">
                  {[
                    'Wound care & dressing',
                    'Injection administration',
                    'Vital signs monitoring',
                    'Post-operative care',
                    'Medical equipment handling',
                    'Pain management'
                  ].map((service, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleBookNow('nurse')}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Book At Home Nurse Services
                </button>
              </motion.div>
            </div>
          </div>

          {/* Baby & Mother Care Section */}
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-8 text-center">Baby & Mother Care</h3>
            <div className="grid md:grid-cols-1 max-w-2xl mx-auto">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Japa Maid Agency in Mumbai Services</h4>
                <ul className="space-y-2 mb-6">
                  {[
                    'Newborn baby care near me',
                    'Mother\'s recovery support',
                    'Feeding & burping assistance',
                    'Baby massage & bathing',
                    'Light household work',
                    'Nutritious meal preparation'
                  ].map((service, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleBookNow('japa')}
                  className="w-full bg-pink-600 text-white py-2 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
                >
                  Book Nanny Services in Mumbai
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Your Caregiver in 3 Simple Steps */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12">
            Get Your Caregiver in 3 Simple Steps
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { step: '1', title: 'Tell Us Your Requirement', description: 'Share your care needs through our booking form or call us directly', icon: FiPhone },
              { step: '2', title: 'Review & Approve Profiles', description: 'We send 2-3 verified caregiver profiles for your approval within 4 hours', icon: FiUsers },
              { step: '3', title: 'Caregiver Starts Work', description: 'Your selected caregiver begins care within 24 hours of approval', icon: FaCheckCircle }
            ].map((step, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 text-center hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  <SafeIcon icon={step.icon} className="w-8 h-8 text-blue-600" />
                </div>
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section id="reviews" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12">
            Customer Reviews
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { rating: 5, review: "Excellent service! The caregiver for my mother is very professional and caring. She handles medication perfectly and is very patient. Highly recommend Shaurya Caretaker!", name: "Priya Sharma", area: "Andheri West", service: "Elderly Care" },
              { rating: 5, review: "Got a japa maid within 24 hours. She's experienced and takes great care of both mother and baby. Very satisfied with the service quality and support team!", name: "Rajesh Kumar", area: "Bandra East", service: "Japa Maid" },
              { rating: 5, review: "Professional nursing care for my father post-surgery. The nurse was skilled in wound dressing and very compassionate. Thank you for the excellent service!", name: "Meera Patel", area: "Juhu", service: "Nursing Care" }
            ].map((review, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="flex items-center mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">"{review.review}"</p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-gray-900">{review.name}</p>
                  <p className="text-gray-600 text-sm">{review.area} • {review.service}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-green-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold mb-2">4.8★</div>
              <div className="text-sm opacity-90">Average Rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">1,000+</div>
              <div className="text-sm opacity-90">Families Served</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">8,000+</div>
              <div className="text-sm opacity-90">Verified Caregivers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Service Areas</h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            We provide home care services across all Mumbai areas including Andheri, Bandra, Borivali, Malad, Kandivali, Juhu, Vile Parle, Goregaon, Jogeshwari, Oshiwara, Versova, Lokhandwala, Four Bungalows and surrounding Mumbai suburbs.
          </p>
          <div className="mt-6">
            {!isMobile ? (
              <button
                onClick={() => scrollToSection('home')}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Check Availability in Your Area
              </button>
            ) : (
              <button
                onClick={openFormModal}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors w-full max-w-xs"
              >
                Check Availability in Your Area
              </button>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {[
              { question: "Are your caregivers verified and trained?", answer: "Yes, all our caregivers undergo thorough background verification, police verification, and professional training. We ensure they have the necessary skills and experience for quality home care." },
              { question: "How quickly can I get a caregiver?", answer: "We provide caregiver placement within 24 hours of your request. In urgent cases, we can arrange care within a few hours." },
              { question: "What if I'm not satisfied with the caregiver?", answer: "We offer free replacement if you're not satisfied with the caregiver. Our goal is your complete satisfaction." },
              { question: "Do you provide services 24/7?", answer: "Yes, we provide both 12-hour and 24-hour care services. Our support team is available 24/7." },
              { question: "What areas in Mumbai do you cover?", answer: "We cover all areas of Mumbai city and surrounding Mumbai suburbs." },
              { question: "Are there any hidden charges?", answer: "No, our pricing is completely transparent with no hidden fees." }
            ].map((faq, index) => (
              <motion.div key={index} className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-4"
                  >
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get the Best Home Care in Mumbai?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join 1,000+ families who trust us for their loved ones' care. Get started today with our free consultation and quick placement guarantee.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+919892330344"
              className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg"
            >
              <SafeIcon icon={FaPhone} className="w-6 h-6" />
              <span>Call Now</span>
            </a>
            <button
              onClick={openFormModal}
              className="flex items-center justify-center space-x-2 bg-green-500 text-white px-8 py-4 rounded-lg hover:bg-green-600 transition-colors font-semibold shadow-lg w-full sm:w-auto"
            >
              <SafeIcon icon={FiEdit} className="w-6 h-6" />
              <span>Get Free Consultation</span>
            </button>
          </div>
          <p className="text-gray-400 text-sm mt-6">
            Response guaranteed within 30 minutes • 24/7 support available
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* Footer Logo - Added to footer */}
              <img src="https://i.ibb.co/mr83wr4d/image2.png" alt="Shaurya Caretaker" className="h-12 w-auto mb-4" />
              <h3 className="text-xl font-bold mb-4">Shaurya Caretaker</h3>
              <p className="text-gray-300 leading-relaxed">
                Mumbai's most trusted home care agency providing quality caregivers for your loved ones since 2014.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Elderly Care</li>
                <li>Patient Care</li>
                <li>Baby Care</li>
                <li>Japa Maid</li>
                <li>Nursing Services</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-300">
                <li>+91 98923 30344</li>
                <li>shauryaagency2018@gmail.com</li>
                <li>Mumbai, Maharashtra</li>
                <li>24/7 Support Available</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button className="hover:text-white transition-colors">Terms of Service</button></li>
                <li><button className="hover:text-white transition-colors">About Us</button></li>
                <li><button className="hover:text-white transition-colors">Careers</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; 2026 <a href="https://iloopworld.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">iloopworldweb</a>. All rights reserved. | Trusted by 1,000+ Mumbai Families</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;